import { seriesCanvasLine } from "d3fc"
import { TimeSeriesData, getGapIndexes, getTimeSeriesData } from "../../../../Data/TimeSeriesData"
import { LineTraceConfig, PageRectangle, RenderStrategy } from "../../../../Types/Trace"
import { TraceRenderStrategy, TraceRendererOptions } from "./RenderStrategy"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { D3Trace } from "./D3Trace"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { ScaleTime } from "d3"
import { activeGoal, rangeOf } from "../../../../Types/GoalRanges"

export class LineRenderStrategy extends TraceRenderStrategy {
	protected directRenderer = seriesCanvasLine()
	protected offscreenRenderer = seriesCanvasLine()
	private pageManager: TimeSeriesPageManager<ModalityPage>
	protected config: LineTraceConfig
	protected d3Trace: D3Trace
	private renderCacheKey: string = ""
	private dataObjectId: number

	constructor(pageManager: TimeSeriesPageManager<ModalityPage>, d3Trace: D3Trace, config: LineTraceConfig) {
		super()
		this.config = config
		this.d3Trace = d3Trace
		this.pageManager = pageManager
		this.dataObjectId = this.d3Trace.getDataObjectId(this.config) ?? Infinity

		this.setupRenderers()
		this.updateRenderCacheKey()
	}

	protected setupRenderers() {
		this.directRenderer = seriesCanvasLine()
		this.offscreenRenderer = seriesCanvasLine()
	}

	public renderPage(page: ModalityPage, reactCallbacks: ModalityGraphGroupReactCallbacks, offscreenXScale: ScaleTime<any, any, any>, offscreenCanvas: OffscreenCanvas, context: CanvasRenderingContext2D, offscreenContext: OffscreenCanvasRenderingContext2D, pageRectangle: PageRectangle, unloadedRectangle: PageRectangle | null) {
		const { x, y, width } = pageRectangle
		const dataObjectId = reactCallbacks.dataSourceMap.get(this.config.dataSource) ?? Infinity
		const traceData = page.data.get(dataObjectId)?.get(this.config.dataKey)
		let edges: [number | undefined, number][] = []

		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		if (traceData !== undefined && traceData.data.length > 0) {
			offscreenXScale.domain([page.startTime, page.endTime]).range([0, width])
			const timeSeriesData = getTimeSeriesData(traceData, this.config)
			edges = this.renderTimeSeriesData(timeSeriesData, page, this.getOffscreenRenderer({ xScale: offscreenXScale, yScale: this.config.yScale }))
		}

		const bitmap = offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(dataObjectId, this.renderCacheKey, { bitmap, dirty: false, edges })
		context.drawImage(bitmap, x, y)
	}
	
	public getRenderCacheKey(): string {
		return this.renderCacheKey
	}

	private updateRenderCacheKey = () => {
		let compositeIndex = undefined

		if (this.config.isCompositePart) {
			compositeIndex = this.config.compositeIndex
		}

		const { graphId, dataKey, dataSource, color } = this.config as LineTraceConfig
		const shading = this.getShadedRange()
		this.renderCacheKey = `${graphId}-${dataKey}-${compositeIndex}-${dataSource}-${color}-${shading ? `${shading.lo}:${shading.hi}` : ""}`
	}

	public updateConfig(traceConfig: LineTraceConfig) {
		this.config = traceConfig
		this.updateRenderCacheKey()
	}

	public getDirectRenderer = (options?: TraceRendererOptions) => {
		if (options?.xScale) {
			this.directRenderer.xScale(options.xScale)
		}

		if (options?.yScale) {
			this.directRenderer.yScale(options.yScale)
		}

		return this.directRenderer
			.mainValue((p: [number, number]) => p[1])
			.crossValue((p: [number, number]) => p[0])
			.decorate((context: CanvasRenderingContext2D) => {
				context.strokeStyle = this.config.color
				context.lineWidth = this.getLineWidth()
			})
	}

	public getOffscreenRenderer = (options?: TraceRendererOptions) => {
		if (options?.xScale) {
			this.offscreenRenderer.xScale(options.xScale)
		}

		if (options?.yScale) {
			this.offscreenRenderer.yScale(options.yScale)
		}

		return this.offscreenRenderer
			.mainValue((p: [number, number]) => p[1])
			.crossValue((p: [number, number]) => p[0])
			.decorate((context: CanvasRenderingContext2D) => {
				context.strokeStyle = this.config.color
				context.lineWidth = this.getLineWidth()
			})
	}

	public render() {
		this.pageManager.getPagesInView().forEach(page => this.d3Trace.renderPage(page))
	}

	public renderTimeSeriesData(data: TimeSeriesData, page: ModalityPage, renderer: (drawableData: Iterable<[(number | undefined), (number | undefined)]>) => void) {
		// If there is only one data point, just pretend that the value is constant.
		if (data.data.length === 1) {
			data = {
				data: new Float32Array([data.data[0], data.data[0]]),
				times: [page.startTime, page.endTime]
			}
		}

		this.shadeBeyondGoal(data, page, renderer)
		renderer(this.drawableDataGenerator(data, page))

		// The edges can be used to redraw the gap between pages when the underlying data is unavailable
		const edges = [
			[data.times[0], data.data[0]], 
			[data.times[data.times.length - 1], data.data[data.data.length - 1]]
		]

		return edges as [number | undefined, number][]
	}

	protected isMissingValue = (value: number | null) => value === null || Number.isNaN(value)

	private *drawableDataGenerator(timeSeriesData: TimeSeriesData, page: ModalityPage): Generator<[number | undefined, number | undefined], void, undefined> {
		const length = timeSeriesData.data.length
		const gapIndexes = getGapIndexes(page.timingInformation.get(this.d3Trace.dataObjectId)?.get(this.config.dataKey)?.gapIndexes ?? [], this.config)
		const samplingPeriod = page.timingInformation.get(this.d3Trace.dataObjectId)?.get(this.config.dataKey)?.samplingPeriod ?? Infinity
		const previousPageData = this.pageManager.getPreviousPage(page)?.data.get(this.dataObjectId)?.get(this.config.dataKey)
		const nextPageData = this.pageManager.getNextPage(page)?.data.get(this.dataObjectId)?.get(this.config.dataKey)

		if (previousPageData) {
			const previousPageTimeSeriesData = getTimeSeriesData(previousPageData, this.config)
			if (previousPageTimeSeriesData.data.length > 0) {
				const previousValue = previousPageTimeSeriesData.data[previousPageTimeSeriesData.data.length-1]
				const previousTime = previousPageTimeSeriesData.times[previousPageTimeSeriesData.times.length-1]
				
				// Check for a gap in times or NaN value
				if (timeSeriesData.times.length > 0 && timeSeriesData.times[0] - previousTime <= 1.05 * samplingPeriod && !Number.isNaN(previousValue)) {
					yield [previousTime, previousValue]
				}
			}
		}

		for (let i = 0; i < length; i++) {
			// Missing values need to be skipped completely
			if (this.isMissingValue(timeSeriesData.data[i])) {
				yield [undefined, undefined]
				continue
			}

			if (gapIndexes.includes(i)) {
				yield [undefined, undefined] // This creates a break in the continuous line
			}

			yield [timeSeriesData.times[i], timeSeriesData.data[i]]
		}

		if (nextPageData) {
			const nextPageTimeSeriesData = getTimeSeriesData(nextPageData, this.config)
			if (nextPageTimeSeriesData.data.length > 0) {
				const nextValue = nextPageTimeSeriesData.data[0]
				const nextTime = nextPageTimeSeriesData.times[0]

				// Check for a gap in times or NaN value
				if (timeSeriesData.times.length > 0 && nextTime - timeSeriesData.times[timeSeriesData.times.length-1] <= 1.05 * samplingPeriod && !Number.isNaN(nextValue)) {
					yield [nextTime, nextValue]
				}
			}
		}
	}

	protected getLineWidth() {
		return 1
	}

	/* The goal range to shade beyond, when the measurement's goal has "shading" on. */
	private getShadedRange() {
		const goal = activeGoal(this.config)
		return goal && goal.shading ? rangeOf(goal) : undefined
	}

	/* Shade the area between the trace and the goal line wherever the trace is beyond it. Drawn
	   under the line, on the same canvas, as polygons clipped to the side beyond the goal. */
	private shadeBeyondGoal(data: TimeSeriesData, page: ModalityPage, renderer: any) {
		const range = this.getShadedRange()
		const context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | undefined = renderer?.context?.()
		const xScale = renderer?.xScale?.()
		const yScale = renderer?.yScale?.()

		if (!range || !context || !xScale || !yScale) {
			return
		}

		const points = [...this.drawableDataGenerator(data, page)]
		const [top, bottom] = [Math.min(...yScale.range()), Math.max(...yScale.range())]
		const left = Math.min(...xScale.range())
		const width = Math.max(...xScale.range()) - left
		const sides: { threshold: number, clipTop: number, clipBottom: number }[] = []

		if (range.hi !== null) {
			sides.push({ threshold: yScale(range.hi), clipTop: top, clipBottom: yScale(range.hi) })
		}
		if (range.lo !== null) {
			sides.push({ threshold: yScale(range.lo), clipTop: yScale(range.lo), clipBottom: bottom })
		}

		// The measurement's own colour, see through, so the line stays readable on top.
		context.save()
		context.globalAlpha = 0.35
		context.fillStyle = this.config.color

		for (const side of sides) {
			context.save()
			context.beginPath()
			context.rect(left, side.clipTop, width, Math.max(0, side.clipBottom - side.clipTop))
			context.clip()

			context.beginPath()
			let open = false
			let lastX = 0
			let lastY = 0
			for (const [time, value] of points) {
				if (time === undefined || value === undefined) {
					if (open) {
						context.lineTo(lastX, side.threshold)
						context.closePath()
						open = false
					}
					continue
				}
				const x = xScale(time)
				const y = yScale(value)
				if (!open) {
					context.moveTo(x, side.threshold)
					open = true
				} else if (this.config.renderStrategy === RenderStrategy.LINE_STEP) {
					context.lineTo(x, lastY)
				}
				context.lineTo(x, y)
				lastX = x
				lastY = y
			}
			if (open) {
				context.lineTo(lastX, side.threshold)
				context.closePath()
			}
			context.fill()
			context.restore()
		}

		context.restore()
	}
}
